import React, { useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import MainLayout from "../../layout/MainLayout";
import { useAuth } from "../../Auths/CheckUser";

const API_ORIGIN ="https://globizsystems.click";
const CHAT_URL = `${API_ORIGIN}/api/chat`;

const toStr = (v) => (v === null || v === undefined ? "" : String(v));

const getLocation = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ lat: null, lng: null });
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        }),
      () => resolve({ lat: null, lng: null }),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  });

export default function ClientReport() {
  const { user } = useAuth();
  const { id } = useParams();
  const location = useLocation();

  // Prefer state passed from RenterDetail
  const client = location.state?.client || null;
  const clientType = location.state?.clientType || "";

  // IMPORTANT: Decide receiverId mapping
  // If your chat receiver should be tenant user id, replace this with client.userId etc.
  const receiverId = toStr(client?.userId || client?.id || id);

  const senderId = toStr(user?.id);

  const cameraRef = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [latlng, setLatlng] = useState({ lat: null, lng: null });
  const [capturedAt, setCapturedAt] = useState(null);

  const [ptpAt, setPtpAt] = useState("");
  const [remarks, setRemarks] = useState("");

  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const openCamera = () => cameraRef.current?.click();

  const handleCapture = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;

    setPhoto(f);
    const { lat, lng } = await getLocation();
    setLatlng({ lat, lng });
    setCapturedAt(new Date().toISOString());
  };

  const buildMessage = () => {
    const lines = [];
    lines.push("📍 LAPORAN LAWATAN");
    lines.push(`ClientID: ${client?.id ?? id}`);
    if (clientType) lines.push(`ClientType: ${clientType}`);
    if (client?.name) lines.push(`Nama: ${client.name}`);
    if (client?.accountNumber) lines.push(`No Akaun: ${client.accountNumber}`);
    if (client?.unitNumber) lines.push(`No Unit: ${client.unitNumber}`);
    if (client?.status) lines.push(`Status: ${client.status}`);

    if (ptpAt) lines.push(`PTP: ${new Date(ptpAt).toLocaleString()}`);
    if (remarks.trim()) lines.push(`Remarks: ${remarks.trim()}`);

    if (capturedAt) lines.push(`CapturedAt: ${new Date(capturedAt).toLocaleString()}`);
    if (latlng.lat != null && latlng.lng != null) {
      lines.push(`Location: ${Number(latlng.lat).toFixed(6)}, ${Number(latlng.lng).toFixed(6)}`);
    }

    return lines.join("\n");
  };

  const sendReport = async () => {
    setErr("");
    setOk("");

    if (!senderId) return setErr("Missing senderId (user.id).");
    if (!receiverId) return setErr("Missing receiverId (map to correct chat user).");
    if (!photo && !ptpAt && !remarks.trim()) {
      return setErr("Please add photo or PTP or remarks.");
    }

    setSending(true);
    try {
      const fd = new FormData();
      fd.append("senderId", senderId);
      fd.append("receiverId", receiverId);
      fd.append("message", buildMessage());

      if (photo) fd.append("file", photo);

      // metadata (backend should store if you want later)
      if (latlng.lat != null) fd.append("lat", String(latlng.lat));
      if (latlng.lng != null) fd.append("lng", String(latlng.lng));
      if (capturedAt) fd.append("capturedAt", capturedAt);

      // optional separate fields
      if (ptpAt) fd.append("ptpAt", new Date(ptpAt).toISOString());
      if (remarks.trim()) fd.append("remarks", remarks.trim());

      await axios.post(CHAT_URL, fd, { headers: { "Content-Type": "multipart/form-data" } });

      setOk("Report sent to chat.");
      setPhoto(null);
      setLatlng({ lat: null, lng: null });
      setCapturedAt(null);
      setPtpAt("");
      setRemarks("");
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "Failed to send report.");
    } finally {
      setSending(false);
    }
  };

  return (
    <MainLayout>
      <div className="p-4">
        <div className="flex items-center justify-between">
          <Link to={-1} className="px-3 py-2 rounded bg-gray-200 text-gray-800">
            Kembali
          </Link>
          <div className="text-sm text-gray-500">Client #{client?.id ?? id}</div>
        </div>

        <div className="mt-4 bg-white rounded-xl shadow p-4">
          <h2 className="text-lg font-bold text-gray-800">Report</h2>
          <p className="text-sm text-gray-500 mt-1">
            Foto + Lokasi + PTP + Remarks
          </p>

          {err && (
            <div className="mt-3 p-3 border border-red-200 bg-red-50 text-red-700 rounded text-sm">
              {err}
            </div>
          )}
          {ok && (
            <div className="mt-3 p-3 border border-green-200 bg-green-50 text-green-700 rounded text-sm">
              {ok}
            </div>
          )}

          {/* Camera */}
          <div className="mt-4 border rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-gray-700">Gambar</div>
              <button
                type="button"
                onClick={openCamera}
                disabled={sending}
                className="px-3 py-2 border rounded hover:bg-gray-50"
              >
                Camera
              </button>
            </div>

            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleCapture}
            />

            {photo ? (
              <div className="mt-3 text-sm">
                <div className="font-medium truncate">{photo.name}</div>
                <div className="mt-2 text-xs text-gray-600">
                  <div>CapturedAt: {capturedAt ? new Date(capturedAt).toLocaleString() : "-"}</div>
                  <div>
                    Location:{" "}
                    {latlng.lat != null && latlng.lng != null
                      ? `${Number(latlng.lat).toFixed(6)}, ${Number(latlng.lng).toFixed(6)}`
                      : "-"}
                  </div>
                </div>

                <button
                  type="button"
                  className="mt-2 underline text-xs text-gray-600"
                  onClick={() => {
                    setPhoto(null);
                    setLatlng({ lat: null, lng: null });
                    setCapturedAt(null);
                  }}
                  disabled={sending}
                >
                  Remove photo
                </button>
              </div>
            ) : (
              <div className="mt-2 text-sm text-gray-500">No photo attached.</div>
            )}
          </div>

          {/* PTP */}
          <div className="mt-4">
            <label className="block text-sm font-semibold text-gray-700 mb-1">PTP</label>
            <input
              type="datetime-local"
              value={ptpAt}
              onChange={(e) => setPtpAt(e.target.value)}
              disabled={sending}
              className="w-full px-3 py-2 border rounded outline-none"
            />
          </div>

          {/* Remarks */}
          <div className="mt-4">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Remarks</label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              disabled={sending}
              rows={4}
              className="w-full px-3 py-2 border rounded outline-none"
              placeholder="Write remarks..."
            />
          </div>

          <button
            type="button"
            onClick={sendReport}
            disabled={sending}
            className="mt-5 w-full py-3 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {sending ? "Sending..." : "Submit"}
          </button>
        </div>
      </div>
    </MainLayout>
  );
}
